/**
 * Ordinamento multi-colonna della lista prodotti.
 *
 * Lo stato è una lista ordinata di criteri (priorità = posizione): un click su una colonna la
 * percorre asc → desc → rimossa; una colonna nuova si accoda in fondo (priorità più bassa).
 * I valori mancanti (es. nessuna scadenza SLA, nessun creatore) stanno sempre in fondo,
 * qualunque sia il verso, perché ordinare per "vuoto" non è mai l'intento.
 */

export type ProductSortKey = 'vuln' | 'sla' | 'id' | 'nome' | 'categoria' | 'creatoDa' | 'creatoIl';

export type SortDir = 'asc' | 'desc';

export interface SortCriterion {
  key: ProductSortKey;
  dir: SortDir;
}

/** Valore confrontabile di una cella; `undefined` = assente (sempre in fondo). */
export type SortValue = number | string | undefined;

/** Click su un'intestazione: assente → asc → desc → rimossa (la posizione si conserva). */
export function toggleSort(criteria: SortCriterion[], key: ProductSortKey): SortCriterion[] {
  const idx = criteria.findIndex((c) => c.key === key);
  if (idx === -1) return [...criteria, { key, dir: 'asc' }];
  const current = criteria[idx]!;
  if (current.dir === 'asc') {
    return criteria.map((c, i) => (i === idx ? { key, dir: 'desc' } : c));
  }
  return criteria.filter((_, i) => i !== idx);
}

const COLLATOR = new Intl.Collator('it', { numeric: true, sensitivity: 'base' });

function compareValues(a: SortValue, b: SortValue, dir: SortDir): number {
  if (a === undefined && b === undefined) return 0;
  if (a === undefined) return 1;
  if (b === undefined) return -1;
  const base =
    typeof a === 'number' && typeof b === 'number' ? a - b : COLLATOR.compare(String(a), String(b));
  return dir === 'asc' ? base : -base;
}

/**
 * Comparatore che applica i criteri in ordine di priorità. A parità su tutti i criteri
 * ritorna 0: con `Array.prototype.sort` stabile resta l'ordine di partenza (quello di default).
 */
export function buildComparator<T>(
  criteria: SortCriterion[],
  valueOf: (item: T, key: ProductSortKey) => SortValue,
): (a: T, b: T) => number {
  return (a, b) => {
    for (const { key, dir } of criteria) {
      const cmp = compareValues(valueOf(a, key), valueOf(b, key), dir);
      if (cmp !== 0) return cmp;
    }
    return 0;
  };
}
